import type { BadgeVariant } from "@freightsimple/ui-core/Display/Badge";
import { CompanyWebhookEventType } from "@freightsimple/generated-dashboard-openapi-client/models/CompanyWebhookEventType";
import { WebhookDeliveryState } from "@freightsimple/generated-dashboard-openapi-client/models/WebhookDeliveryState";
import { WebhookDeliveryTrigger } from "@freightsimple/generated-dashboard-openapi-client/models/WebhookDeliveryTrigger";

/**
 * Customer-facing names for the webhook enums. The Dashboard shows the database names the
 * customer also sees in Hubble screenshots from ops, not the `shipment_`-prefixed names the
 * webhook payload's `event_type` carries.
 */

const eventTypeLabels: Record<CompanyWebhookEventType, string> = {
  [CompanyWebhookEventType.ShipmentBooked]: "Shipment booked",
  [CompanyWebhookEventType.ShipmentQuoted]: "Shipment quoted",
  [CompanyWebhookEventType.ShipmentInTransit]: "Shipment in transit",
  [CompanyWebhookEventType.ShipmentDelivered]: "Shipment delivered",
  [CompanyWebhookEventType.ShipmentCancelled]: "Shipment cancelled",
  [CompanyWebhookEventType.ShipmentOnHold]: "Shipment on hold",
  [CompanyWebhookEventType.DraftCreated]: "Draft created",
  [CompanyWebhookEventType.DocumentUploaded]: "Document uploaded",
  [CompanyWebhookEventType.InvoicesUpdated]: "Invoices updated",
  [CompanyWebhookEventType.TrackingLineAdded]: "Tracking line added",
};

export function describeWebhookEventType(
  eventType: CompanyWebhookEventType,
): string {
  return eventTypeLabels[eventType];
}

const stateLabels: Record<WebhookDeliveryState, string> = {
  [WebhookDeliveryState.Pending]: "Retrying",
  [WebhookDeliveryState.Delivered]: "Delivered",
  [WebhookDeliveryState.Failed]: "Failed",
  [WebhookDeliveryState.Exhausted]: "Gave up",
  [WebhookDeliveryState.Superseded]: "Replaced",
};

export function describeWebhookDeliveryState(
  state: WebhookDeliveryState,
): string {
  return stateLabels[state];
}

const stateVariants: Record<WebhookDeliveryState, BadgeVariant> = {
  [WebhookDeliveryState.Pending]: "info",
  [WebhookDeliveryState.Delivered]: "success",
  [WebhookDeliveryState.Failed]: "danger",
  [WebhookDeliveryState.Exhausted]: "danger",
  // Not a failure: a newer update for the same shipment went out instead.
  [WebhookDeliveryState.Superseded]: "neutral",
};

export function webhookDeliveryStateVariant(
  state: WebhookDeliveryState,
): BadgeVariant {
  return stateVariants[state];
}

const triggerLabels: Record<WebhookDeliveryTrigger, string> = {
  [WebhookDeliveryTrigger.System]: "Event",
  [WebhookDeliveryTrigger.Customer]: "Your redelivery",
  [WebhookDeliveryTrigger.Ops]: "FreightSimple redelivery",
};

export function describeWebhookDeliveryTrigger(
  trigger: WebhookDeliveryTrigger,
): string {
  return triggerLabels[trigger];
}
